import { getCookie, removeCookie, setCookie } from "../cookies/cookies";
import { getUTCDate } from "../getUTCDate/getUTCDate";

export const SAV_BLOCKED_UNTIL_COOKIE = "savBlockedUntil";

const MS_PER_MINUTE = 60 * 1000;

const parseTimestamp = (value: string | boolean | Record<string, string | boolean> | undefined): number | null => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
};

export const setSavBlockFallbackStorage = (blockedUntil: number) => {
  try {
    sessionStorage.setItem(SAV_BLOCKED_UNTIL_COOKIE, String(blockedUntil));
  } catch {
    // storage unavailable (SSR / partitioned iframe / private mode) — fallback is best-effort
  }

  try {
    localStorage.setItem(SAV_BLOCKED_UNTIL_COOKIE, String(blockedUntil));
  } catch {
    // storage unavailable (SSR / partitioned iframe / private mode) — fallback is best-effort
  }
};

const getSavBlockedUntilFromCookie = (): number | null => {
  try {
    return parseTimestamp(getCookie(SAV_BLOCKED_UNTIL_COOKIE));
  } catch {
    return null;
  }
};

const getSavBlockedUntilFromStorage = (): number | null => {
  try {
    const fromSession = parseTimestamp(sessionStorage.getItem(SAV_BLOCKED_UNTIL_COOKIE) ?? undefined);
    if (fromSession) {
      return fromSession;
    }
  } catch {
    // storage unavailable — fall through to localStorage
  }

  try {
    return parseTimestamp(localStorage.getItem(SAV_BLOCKED_UNTIL_COOKIE) ?? undefined);
  } catch {
    return null;
  }
};

export const getSavBlockedUntil = (): number | null => {
  return getSavBlockedUntilFromCookie() ?? getSavBlockedUntilFromStorage();
};

export const isSavBlocked = (): boolean => {
  const blockedUntil = getSavBlockedUntil();
  return blockedUntil !== null && Date.now() < blockedUntil;
};

export const setSavBlock = (durationInMinutes: number) => {
  // Enforce a one-minute minimum so an enabled block cannot expire immediately.
  const ttlInMs = Math.max(1, durationInMinutes) * MS_PER_MINUTE;
  const blockedUntil = Date.now() + ttlInMs;

  setCookie(SAV_BLOCKED_UNTIL_COOKIE, String(blockedUntil), "/", getUTCDate(ttlInMs), {
    sameSite: "None",
    secure: true,
  });

  /*
  In cross-site iframe contexts (e.g. Builder-embedded), WebKit/Safari can silently
  reject the SameSite=None cookie write, so fall back to session/local storage only
  when the cookie did not persist. Mirrors the age-confirmation persistence.
  */
  if (!getSavBlockedUntilFromCookie()) {
    setSavBlockFallbackStorage(blockedUntil);
  }

  return blockedUntil;
};

export const clearSavBlock = () => {
  removeCookie(SAV_BLOCKED_UNTIL_COOKIE, "/");

  try {
    sessionStorage.removeItem(SAV_BLOCKED_UNTIL_COOKIE);
  } catch {
    // storage unavailable — nothing to remove
  }

  try {
    localStorage.removeItem(SAV_BLOCKED_UNTIL_COOKIE);
  } catch {
    // storage unavailable — nothing to remove
  }
};
